import { useState } from 'react';
import { useStore } from '../../store/useStore';
import { generarMallaTEA } from '../../data/seedTecnicatura';
import { generarMallaPsico2013, OpcionesGeneracionPsico } from '../../data/mallaPsico2013';
import { ConfiguracionPsico } from './ConfiguracionPsico';
import { SignInButton, SignUpButton } from '@clerk/clerk-react';
import { 
  ArrowLeft, LogIn, ChevronRight, User, Laptop, Cloud, Sparkles, Check, 
  CheckCircle2, AlertCircle, Landmark, Brain
} from 'lucide-react';
import { cn } from '../../lib/utils';

export function OnboardingScreen() {
  const addAsignaturas = useStore(s => s.addAsignaturas);
  const loadData = useStore(s => s.loadData);
  const setActiveFacultad = useStore(s => s.setActiveFacultad);
  const setActiveCarrera = useStore(s => s.setActiveCarrera);
  const storeCarreras = useStore(s => s.carreras);
  const defaultCarreras = [
    { id: 'carrera_fcea_tea', facultad_id: 'fcea', nombre: 'Tecnicatura en Administración', tipo: 'tecnicatura', creditos_totales: 150, duracion_semestres: 5, locacion: 'Montevideo', malla_precargada: true },
    { id: 'carrera_fcea_cp', facultad_id: 'fcea', nombre: 'Contador Público', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: false },
    { id: 'carrera_fcea_la', facultad_id: 'fcea', nombre: 'Licenciatura en Administración', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: false },
    { id: 'carrera_fcea_le', facultad_id: 'fcea', nombre: 'Licenciatura en Economía', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: false },
    { id: 'carrera_psico_lic', facultad_id: 'psico', nombre: 'Licenciatura en Psicología', tipo: 'licenciatura', creditos_totales: 320, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: true }
  ];
  const carreras = storeCarreras.length > 0 ? storeCarreras : defaultCarreras;
  const startDemoMode = useStore(s => s.startDemoMode);

  const [loading, setLoading] = useState(false);
  const [selectedFacultad, setSelectedFacultad] = useState<'fcea' | 'psico' | null>(null);
  const [hoveredFacultad, setHoveredFacultad] = useState<'fcea' | 'psico' | null>(null);
  const [selectedCalculo, setSelectedCalculo] = useState<'unificado' | 'desdoblado'>('unificado');
  const [step, setStep] = useState<'landing' | 'facultades' | 'carreras' | 'config_tea' | 'config_psico'>('landing');

  const handleComenzarTEA = async () => {
    setLoading(true);
    try {
      setActiveFacultad('fcea');
      setActiveCarrera('carrera_fcea_tea');
      const malla = generarMallaTEA(selectedCalculo);
      await addAsignaturas(malla);
      localStorage.setItem('fcea_onboarding_skipped', 'true');
      await loadData();
    } catch (error) {
      console.error('Error importing seed tecnicatura:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComenzarPsico = async (opciones: OpcionesGeneracionPsico) => {
    setLoading(true);
    try {
      setActiveFacultad('psico');
      setActiveCarrera('carrera_psico_lic');
      const malla = generarMallaPsico2013(opciones);
      await addAsignaturas(malla);
      localStorage.setItem('fcea_onboarding_skipped', 'true');
      await loadData();
    } catch (error) {
      console.error('Error importing seed psicología:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEmpty = async (facultadId: string, carreraId: string | null) => {
    setLoading(true);
    try {
      setActiveFacultad(facultadId);
      setActiveCarrera(carreraId);
      localStorage.setItem('fcea_onboarding_skipped', 'true');
      await loadData();
    } catch (error) {
      console.error('Error initializing empty space:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFacultad = (facId: 'fcea' | 'psico') => {
    setSelectedFacultad(facId);
    setStep('carreras');
  };

  const getStepProgress = () => {
    switch (step) {
      case 'facultades': return { current: 1, total: 3, label: 'Paso 1 de 3: Facultad' };
      case 'carreras': return { current: 2, total: 3, label: 'Paso 2 de 3: Carrera' };
      case 'config_tea': return { current: 3, total: 3, label: 'Paso 3 de 3: Plan' };
      default: return null;
    }
  };

  const progress = getStepProgress();

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-between bg-[#F8FAFC] p-3 sm:p-4 select-none font-sans overflow-hidden selection:bg-[#EA514D]/20">
      
      {/* Custom Keyframe Animations & Glass Styling */}
      <style>{`
        .animate-float {
            animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
            animation: float 6s ease-in-out 3s infinite;
        }
        .animate-pulse-slow {
            animation: pulse-slow 8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes float {
            0% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-12px) rotate(2deg); }
            100% { transform: translateY(0px) rotate(0deg); }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 0.1; transform: scale(1); }
            50% { opacity: 0.2; transform: scale(1.05); }
        }
        .organic-shape {
            border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
            transition: all 0.5s ease;
        }
        .organic-shape:hover {
            border-radius: 60% 40% 50% 50% / 50% 50% 60% 40%;
        }
        .glass-panel:hover .glass-pane {
            opacity: 1;
        }
        .glass-card {
            background: rgba(255, 255, 255, 0.75);
            backdrop-filter: blur(24px);
            -webkit-backdrop-filter: blur(24px);
            border: 1px solid rgba(255, 255, 255, 0.9);
            box-shadow: 0 10px 40px -10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,1);
        }
        .bg-mesh-layer {
            position: fixed;
            inset: -20%;
            z-index: 0;
            transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            filter: blur(120px);
            pointer-events: none;
        }
        .bg-mesh-default {
            background: radial-gradient(circle at 20% 20%, #dce9ff 0%, transparent 40%),
                        radial-gradient(circle at 80% 80%, #eff4ff 0%, transparent 40%);
            opacity: 1;
        }
        .bg-mesh-fcea {
            background: radial-gradient(circle at 30% 20%, rgba(234, 81, 77, 0.25) 0%, transparent 50%),
                        radial-gradient(circle at 70% 80%, rgba(56, 72, 105, 0.25) 0%, transparent 50%);
            opacity: 0;
        }
        .bg-mesh-psico {
            background: radial-gradient(circle at 70% 20%, rgba(0, 153, 169, 0.25) 0%, transparent 50%),
                        radial-gradient(circle at 30% 80%, rgba(178, 214, 210, 0.35) 0%, transparent 50%);
            opacity: 0;
        }
        .bubble-float { animation: float 6s ease-in-out infinite; }
        .bubble-float:nth-child(2) { animation-delay: -2s; }
        .bubble-float:nth-child(3) { animation-delay: -4s; }
        .bubble-float:nth-child(4) { animation-delay: -1s; }
        .btn-glow {
            position: relative;
            overflow: hidden;
        }
        .btn-glow::after {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(234, 81, 77, 0.3) 0%, transparent 70%);
            opacity: 0;
            transition: opacity 0.3s ease;
        }
        .btn-glow:hover::after {
            opacity: 1;
        }
        .elastic-transition {
            transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>

      {/* Ambient Mesh Backgrounds */}
      <div className={`bg-mesh-layer bg-mesh-default ${hoveredFacultad ? 'opacity-0' : 'opacity-100'}`}></div>
      <div className={`bg-mesh-layer bg-mesh-fcea ${hoveredFacultad === 'fcea' ? 'opacity-100' : 'opacity-0'}`}></div>
      <div className={`bg-mesh-layer bg-mesh-psico ${hoveredFacultad === 'psico' ? 'opacity-100' : 'opacity-0'}`}></div>

      {/* Header Navigation Bar */}
      <div className="w-full max-w-5xl mx-auto flex items-center justify-between relative z-20 pt-2 px-2">
        {step !== 'landing' ? (
          <button
            onClick={() => {
              if (step === 'facultades') setStep('landing');
              else if (step === 'carreras') setStep('facultades');
              else if (step === 'config_tea') setStep('carreras');
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 bg-white/80 hover:bg-white backdrop-blur-md border border-slate-200/80 rounded-full transition-all shadow-2xs group cursor-pointer active:scale-95"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            <span>Volver</span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-extrabold font-mono text-xs tracking-widest uppercase transition-colors",
              selectedFacultad === 'psico' ? "text-[#0099A9]" : "text-[#EA514D]"
            )}>MALLA 2026</span>
          </div>
        )}

        {progress && (
          <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-white/80 backdrop-blur-md rounded-full border border-slate-200/80 shadow-2xs font-mono text-[11px] font-bold text-slate-700">
            <span className={cn(
              "w-2 h-2 rounded-full animate-pulse transition-colors",
              selectedFacultad === 'psico' ? "bg-[#0099A9]" : "bg-[#EA514D]"
            )}></span>
            {progress.label}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <main className="w-full max-w-5xl my-auto relative z-10 flex flex-col items-center justify-center py-1">
        {loading ? (
          <div className="glass-card flex flex-col items-center justify-center p-6 gap-2 rounded-3xl min-h-[200px] w-full max-w-md text-center shadow-xl border border-white">
            <div className={cn(
              "w-10 h-10 border-3 border-t-transparent rounded-full animate-spin transition-colors",
              selectedFacultad === 'psico' ? "border-[#0099A9]" : "border-[#EA514D]"
            )}></div>
            <p className="text-sm font-bold text-slate-900">Configurando tu espacio de trabajo...</p>
            <p className="text-xs text-slate-500 max-w-xs">Cargando tokens institucionales y estructurando el plan académico.</p>
          </div>
        ) : step === 'landing' ? (

          /* SCREEN 1: LANDING INTERACCIÓN ORGÁNICA (GLASS) */
          <div className="w-full flex flex-col items-center animate-in fade-in duration-500">
            {/* Header / Brand */}
            <div className="text-center mb-4">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 mb-1">Bienvenido a Malla</h1>
              <p className="text-xs text-slate-600 max-w-md mx-auto">
                Plataforma de seguimiento académico inteligente para estudiantes de Udelar.
              </p>
            </div>

            {/* Central Glass Stage */}
            <div className="relative w-full max-w-4xl bg-white/30 backdrop-blur-3xl rounded-[2rem] p-3 sm:p-4 shadow-2xl border border-white/80 flex flex-col md:flex-row gap-3 items-stretch group/stage overflow-hidden">
              
              {/* Left Card: Authenticated (Clerk) */}
              <div className="relative z-10 flex-1 bg-white/60 backdrop-blur-xl rounded-[2rem] p-4 sm:p-5 shadow-sm border border-white flex flex-col items-center justify-between min-h-[280px] transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:bg-white/80 group glass-panel">
                <div className="w-14 h-14 bg-[#EA514D]/10 organic-shape flex items-center justify-center mb-2 shadow-sm group-hover:bg-[#EA514D]/20 transition-colors duration-500 group-hover:scale-110">
                  <Cloud className="w-7 h-7 text-[#EA514D] transition-transform duration-500 group-hover:rotate-12" />
                </div>
                <h2 className="text-sm font-bold text-slate-900 mb-0.5 text-center flex items-center gap-1.5">
                  <User size={16} className="text-[#EA514D]" />
                  Opción 1: Iniciar Sesión o Registrarse
                </h2>
                <div className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-3 py-0.5 rounded-full mb-2 uppercase tracking-wider">
                  Recomendado
                </div>
                <p className="text-center text-[11px] text-slate-600 mb-3 max-w-xs leading-relaxed">
                  Sincronizá tus materias y notas en la nube de forma segura y accedé desde cualquier dispositivo.
                </p>
                
                <div className="flex flex-col w-full gap-2 max-w-xs mt-auto">
                  <SignInButton mode="modal">
                    <button className="w-full bg-primary hover:bg-primary-container text-white h-9 rounded-full text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg active:scale-95 cursor-pointer">
                      <LogIn size={16} />
                      Iniciar Sesión
                    </button>
                  </SignInButton>

                  <SignUpButton mode="modal">
                    <button className="w-full bg-slate-900 hover:bg-slate-800 text-white h-9 rounded-full text-xs font-bold flex items-center justify-center transition-all shadow-sm active:scale-95 cursor-pointer">
                      Registrarse Gratis
                    </button>
                  </SignUpButton>
                </div>
              </div>

              {/* Desktop Divider */}
              <div className="hidden md:flex items-center justify-center w-4 relative z-10">
                <div className="w-px h-2/3 bg-gradient-to-b from-transparent via-slate-300/60 to-transparent"></div>
              </div>

              {/* Right Card: Guest */}
              <div className="relative z-10 flex-1 bg-white/60 backdrop-blur-xl rounded-[2rem] p-4 sm:p-5 shadow-sm border border-white flex flex-col items-center justify-between min-h-[280px] transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:bg-white/80 group glass-panel">
                <h2 className="text-sm font-bold text-slate-900 mb-0.5 text-center flex items-center gap-1.5 mt-1">
                  <Laptop size={16} className="text-slate-700" />
                  Opción 2: Continuar como Invitado
                </h2>
                <div className="bg-slate-200/80 text-slate-700 text-[10px] font-bold px-3 py-0.5 rounded-full mb-2 uppercase tracking-wider">
                  Modo Local-First
                </div>

                <button
                  onClick={() => setStep('facultades')}
                  className="w-full max-w-xs bg-white hover:bg-slate-50 text-slate-800 h-9 rounded-full text-xs font-bold flex items-center justify-between px-5 transition-all shadow-md hover:shadow-lg border border-slate-200 group/btn mb-3 active:scale-95 cursor-pointer"
                >
                  <span>Continuar como Invitado (Configurar Malla)</span>
                  <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform text-slate-600" />
                </button>

                {/* Floating Pills Grid */}
                <div className="grid grid-cols-2 gap-2 w-full max-w-xs mt-auto">
                  <div className="bg-white/80 border border-white backdrop-blur-xl p-2 rounded-xl shadow-2xs flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                    <span className="text-[10px] font-medium text-slate-700 leading-tight">100% sin conexión a internet</span>
                  </div>
                  <div className="bg-white/80 border border-white backdrop-blur-xl p-2 rounded-xl shadow-2xs flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                    <span className="text-[10px] font-medium text-slate-700 leading-tight">Datos guardados en tu compu</span>
                  </div>
                  <div className="bg-white/80 border border-white backdrop-blur-xl p-2 rounded-xl shadow-2xs flex items-center gap-2">
                    <AlertCircle size={14} className="text-[#EA514D] shrink-0" />
                    <span className="text-[10px] font-medium text-slate-700 leading-tight">Sin respaldo en la nube</span>
                  </div>
                  <div className="bg-white/80 border border-white backdrop-blur-xl p-2 rounded-xl shadow-2xs flex items-center gap-2">
                    <AlertCircle size={14} className="text-[#EA514D] shrink-0" />
                    <span className="text-[10px] font-medium text-slate-700 leading-tight">Sin acceso multi-dispositivo</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Demo Button */}
            <div className="mt-3">
              <button
                onClick={startDemoMode}
                className="flex items-center gap-2 text-slate-600 hover:text-[#EA514D] transition-colors text-xs font-semibold bg-white/80 backdrop-blur-xl px-5 py-2 rounded-full shadow-md border border-white/80 hover:scale-105 active:scale-95 duration-200 cursor-pointer"
              >
                <Sparkles size={14} className="text-[#EA514D]" />
                Ver un recorrido de ejemplo (Modo Demo Aislado)
              </button>
            </div>
          </div>

        ) : step === 'facultades' ? (

          /* SCREEN 2: SELECCIÓN DE FACULTAD (OPTIMIZADO CON ICONOS) */
          <div className="w-full flex flex-col items-center animate-in fade-in duration-500 max-w-4xl">
            <header className="text-center mb-5">
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Seleccioná tu Facultad</h1>
              <p className="text-xs text-slate-600 max-w-lg mx-auto">Elegí tu facultad para personalizar la experiencia e indicadores académicos en Malla.</p>
            </header>

            {/* Active Faculty Cards */}
            <div className="flex flex-col md:flex-row mb-6 w-full justify-center items-stretch gap-4 sm:gap-6">
              
              {/* FCEA Card */}
              <button
                onMouseEnter={() => setHoveredFacultad('fcea')}
                onMouseLeave={() => setHoveredFacultad(null)}
                onClick={() => handleSelectFacultad('fcea')}
                className="fcea-card group relative text-left glass-card rounded-[2rem] p-5 w-full transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 focus:outline-none -rotate-1 hover:rotate-0 flex flex-col justify-between min-h-[10rem] md:w-72 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-[2rem] border-2 border-transparent group-hover:border-[#EA514D]/30 transition-colors duration-500 pointer-events-none"></div>
                <div className="flex items-start justify-between mb-3 relative z-10">
                  <div className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center text-slate-700 group-hover:bg-gradient-to-br group-hover:from-[#EA514D] group-hover:to-[#384869] group-hover:text-white transition-all duration-500 group-hover:shadow-lg group-hover:shadow-[#EA514D]/20">
                    <Landmark className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <span className="text-[11px] font-bold bg-white/80 backdrop-blur-sm text-slate-700 px-3 py-1 rounded-full border border-slate-200/80 group-hover:text-[#EA514D] group-hover:border-[#EA514D]/30 transition-all duration-500 shadow-2xs">Disponible</span>
                </div>
                <div className="relative z-10">
                  <h2 className="text-xl font-bold text-slate-900 group-hover:text-[#EA514D] transition-colors duration-300 mb-1">FCEA</h2>
                  <p className="text-xs text-slate-600 leading-relaxed">Facultad de Ciencias Económicas y de Administración</p>
                </div>
              </button>

              {/* Psicología Card */}
              <button
                onMouseEnter={() => setHoveredFacultad('psico')}
                onMouseLeave={() => setHoveredFacultad(null)}
                onClick={() => handleSelectFacultad('psico')}
                className="psico-card group relative text-left glass-card rounded-[2rem] p-5 w-full transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 focus:outline-none rotate-1 hover:rotate-0 flex flex-col justify-between min-h-[10rem] md:w-72 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-[2rem] border-2 border-transparent group-hover:border-[#0099A9]/30 transition-colors duration-500 pointer-events-none"></div>
                <div className="flex items-start justify-between mb-3 relative z-10">
                  <div className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center text-slate-700 group-hover:bg-gradient-to-br group-hover:from-[#0099A9] group-hover:to-[#B2D6D2] group-hover:text-white transition-all duration-500 group-hover:shadow-lg group-hover:shadow-[#0099A9]/20">
                    <Brain className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <span className="text-[11px] font-bold bg-white/80 backdrop-blur-sm text-slate-700 px-3 py-1 rounded-full border border-slate-200/80 group-hover:text-[#0099A9] group-hover:border-[#0099A9]/30 transition-all duration-500 shadow-2xs">Disponible</span>
                </div>
                <div className="relative z-10">
                  <h2 className="text-xl font-bold text-slate-900 group-hover:text-[#0099A9] transition-colors duration-300 mb-1">PSICO</h2>
                  <p className="text-xs text-slate-600 leading-relaxed">Facultad de Psicología</p>
                </div>
              </button>

            </div>

            {/* Próximamente Floating Bubbles */}
            <div className="w-full flex flex-col items-center">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-2 relative">
                — Próximamente —
              </span>
              <div className="flex flex-wrap justify-center gap-3 max-w-xl opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500">
                <div className="bubble-float glass-card rounded-full px-4 py-2 flex items-center gap-2 cursor-not-allowed">
                  <span className="font-mono text-xs font-bold text-slate-700">FING</span>
                </div>
                <div className="bubble-float glass-card rounded-full px-4 py-2 flex items-center gap-2 cursor-not-allowed">
                  <span className="font-mono text-xs font-bold text-slate-700">FMED</span>
                </div>
                <div className="bubble-float glass-card rounded-full px-4 py-2 flex items-center gap-2 cursor-not-allowed">
                  <span className="font-mono text-xs font-bold text-slate-700">FDER</span>
                </div>
                <div className="bubble-float glass-card rounded-full px-4 py-2 flex items-center gap-2 cursor-not-allowed">
                  <span className="font-mono text-xs font-bold text-slate-700">FQUI</span>
                </div>
              </div>
            </div>
          </div>

        ) : step === 'carreras' ? (

          /* SCREEN 3: SELECCIÓN DE CARRERA (REFINADO FCEA FLUID) */
          <div className="w-full flex flex-col items-center animate-in fade-in duration-500 max-w-2xl">
            <header className="text-center mb-4">
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Seleccioná tu Carrera</h1>
              <p className="text-xs text-slate-600">Elegí la oferta académica correspondiente a tu plan de estudios.</p>
            </header>

            <div className="grid grid-cols-1 gap-3 w-full">
              {carreras
                .filter(c => !selectedFacultad || c.facultad_id === selectedFacultad)
                .map(carrera => {
                  const isPsico = carrera.facultad_id === 'psico' || selectedFacultad === 'psico';
                  return (
                    <button
                      key={carrera.id}
                      onClick={() => {
                        if (carrera.id === 'carrera_fcea_tea') {
                          setStep('config_tea');
                        } else if (carrera.id === 'carrera_psico_lic') {
                          setStep('config_psico');
                        } else {
                          handleStartEmpty(carrera.facultad_id, carrera.id);
                        }
                      }}
                      className={cn(
                        "glass-card p-4 rounded-xl border border-white/80 text-left transition-all duration-300 hover:scale-[1.01] hover:bg-white/90 shadow-sm flex items-center justify-between group cursor-pointer",
                        isPsico ? "hover:border-[#0099A9]/40" : "hover:border-[#EA514D]/40"
                      )}
                    >
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn(
                            "text-base font-bold transition-colors",
                            isPsico ? "text-slate-900 group-hover:text-[#0099A9]" : "text-slate-900 group-hover:text-[#EA514D]"
                          )}>
                            {carrera.nombre}
                          </span>
                          {carrera.malla_precargada && (
                            <span className={cn(
                              "text-[10px] font-bold px-2.5 py-0.5 rounded-full border transition-colors",
                              isPsico
                                ? "bg-[#0099A9]/10 text-[#0099A9] border-[#0099A9]/20"
                                : "bg-[#EA514D]/10 text-[#EA514D] border-[#EA514D]/20"
                            )}>
                              Malla Interactiva Precargada
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">Plan de estudios oficial de Udelar</p>
                      </div>
                      <ChevronRight
                        size={20}
                        className={cn(
                          "text-slate-400 group-hover:translate-x-1 transition-all shrink-0",
                          isPsico ? "group-hover:text-[#0099A9]" : "group-hover:text-[#EA514D]"
                        )}
                      />
                    </button>
                  );
                })}
            </div>
          </div>

        ) : step === 'config_tea' ? (

          /* SCREEN 4: ONBOARDING MATEMÁTICA (FLUID MOTION - RECTO Y REFINADO SEGÚN FCEA DESIGN) */
          <div className="w-full flex flex-col items-center animate-in fade-in duration-500 max-w-xl">
            <header className="text-center mb-4">
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Modalidad de Matemática</h1>
              <p className="text-xs text-slate-600">Configurá la modalidad de Cálculo para evitar duplicación de créditos.</p>
            </header>

            {/* Card siempre RECTA sin rotación */}
            <div className="glass-card rounded-[2rem] p-5 sm:p-6 w-full border border-white shadow-xl relative z-10 rotate-0">
              <h2 className="text-base font-bold text-slate-900 mb-0.5">¿Cómo vas a cursar Matemática?</h2>
              <p className="text-xs text-slate-500 mb-4">Podés cambiar o ajustar este plan en cualquier momento desde el Roadmap.</p>

              <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); handleComenzarTEA(); }}>
                
                {/* Option 1: Unificado */}
                <div className="relative group">
                  <input
                    type="radio"
                    id="track_1"
                    name="math_track"
                    className="hidden peer"
                    checked={selectedCalculo === 'unificado'}
                    onChange={() => setSelectedCalculo('unificado')}
                  />
                  <label
                    htmlFor="track_1"
                    className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all cursor-pointer elastic-transition group ${
                      selectedCalculo === 'unificado'
                        ? 'border-[#EA514D] bg-[#EA514D]/10 ring-2 ring-[#EA514D]/20 shadow-sm'
                        : 'border-slate-200/80 bg-white/60 hover:bg-white/90 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                        selectedCalculo === 'unificado' ? 'bg-[#EA514D] text-white shadow-xs' : 'border-2 border-slate-300 bg-white'
                      }`}>
                        {selectedCalculo === 'unificado' && <Check size={14} className="stroke-[3]" />}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900">Cálculo I (Semestral)</span>
                        <span className="text-xs text-slate-500">Opción directa unificada en Semestre 1</span>
                      </div>
                    </div>
                    {/* Badge de Créditos */}
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-mono font-bold bg-[#EA514D]/15 text-[#EA514D] border border-[#EA514D]/30 shrink-0">
                      10 créditos
                    </span>
                  </label>
                </div>

                {/* Option 2: Desdoblado */}
                <div className="relative group">
                  <input
                    type="radio"
                    id="track_2"
                    name="math_track"
                    className="hidden peer"
                    checked={selectedCalculo === 'desdoblado'}
                    onChange={() => setSelectedCalculo('desdoblado')}
                  />
                  <label
                    htmlFor="track_2"
                    className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all cursor-pointer elastic-transition group ${
                      selectedCalculo === 'desdoblado'
                        ? 'border-[#EA514D] bg-[#EA514D]/10 ring-2 ring-[#EA514D]/20 shadow-sm'
                        : 'border-slate-200/80 bg-white/60 hover:bg-white/90 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                        selectedCalculo === 'desdoblado' ? 'bg-[#EA514D] text-white shadow-xs' : 'border-2 border-slate-300 bg-white'
                      }`}>
                        {selectedCalculo === 'desdoblado' && <Check size={14} className="stroke-[3]" />}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900">Cálculo I/A y Cálculo I/B (Anual)</span>
                        <span className="text-xs text-slate-500">Modalidad desdoblada en dos cursos</span>
                      </div>
                    </div>
                    {/* Badge de Créditos */}
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-mono font-bold bg-[#EA514D]/15 text-[#EA514D] border border-[#EA514D]/30 shrink-0">
                      5 créditos cada uno
                    </span>
                  </label>
                </div>

                {/* Submit Actions */}
                <div className="flex gap-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setStep('carreras')}
                    className="flex-1 py-2.5 text-xs font-bold rounded-xl bg-slate-200/70 text-slate-700 hover:bg-slate-200 transition-colors active:scale-95 duration-200 cursor-pointer"
                  >
                    Volver
                  </button>
                  <button
                    type="submit"
                    className="flex-[2] py-2.5 text-xs font-bold rounded-xl bg-primary hover:bg-primary-container text-white shadow-lg shadow-primary/25 btn-glow hover:translate-y-[-2px] active:scale-95 transition-all duration-300 cursor-pointer"
                  >
                    Comenzar Carrera
                  </button>
                </div>
              </form>
            </div>
          </div>

        ) : step === 'config_psico' ? (
          <ConfiguracionPsico
            onConfirm={handleComenzarPsico}
            onBack={() => setStep('carreras')}
          />
        ) : null}
      </main>

      {/* Global Fixed Footer Legal Notice (Single Source across all Onboarding views) */}
      <footer className="w-full text-center py-2 relative z-20 pointer-events-auto">
        <p className="text-[10px] font-mono tracking-widest text-slate-400 uppercase opacity-75 hover:opacity-100 transition-opacity">
          © 2026 PLATAFORMA MALLA — PROYECTO ESTUDIANTIL INDEPENDIENTE — SIN VÍNCULO OFICIAL CON UDELAR
        </p>
      </footer>
    </div>
  );
}
